import React, { useContext, useEffect, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import {
  Modal,
  Box,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import NeoButton from "./NeoButton";
import { useRoomsStore } from "../@shared/store/useRoomsStore";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { MainContext } from "../App";
import Toast from "./Toast";


const screenHeight = window.innerHeight;

const modalHeight = screenHeight * 0.6;
const modalWidth = modalHeight * (4 / 5);

const backgroundImage = require("../@shared/assets/game_menu.png");

const JoinRoomModal = ({ visible, onClose, userId }) => {
  const { userData } = useAuthStore();
  const [codeInput, setCodeInput] = useState("");
  const fetchLobbyRooms = useRoomsStore((s) => s.fetchLobbyRooms);
  const joinAndSetRoom = useRoomsStore((s) => s.joinAndSetRoom);
  const rooms = useRoomsStore((s) => s.rooms);
  const clearRooms = useRoomsStore((s) => s.clearRooms);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { Theme, setScreenLoading } = useContext(MainContext);
  const { setMessage, setError, setInfo, setSuccess } = useContext(MainContext);

  useEffect(() => {
    if (visible) {
      setLoading(true);
      console.log("Fetching lobby rooms...");
      fetchLobbyRooms().finally(() => setLoading(false));
    }
  }, [visible, fetchLobbyRooms]);

  const handleCodeJoin = async () => {
    const accessCode = Number(codeInput);
    try {
      const q = query(
        collection(db, "rooms"),
        where("accessCode", "==", accessCode)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        console.warn("Room not found");
        return;
      }
      const roomDoc = snapshot.docs[0];
      const roomData = roomDoc.data();

      const humanPlayers = roomData.players.length;
      const maxHumansAllowed = Math.floor(roomData.amountOfPlayers / 2);

      if (roomData.pve && humanPlayers >= maxHumansAllowed) {

        setMessage("Join failed: PvE mode only allows a limited number of human players.");
        setError(true);
        return;
      }

      if (roomData.players.length >= roomData.amountOfPlayers) {

        setMessage("Join failed: Room is full.");
        setError(true);
        return;
      }
      await joinAndSetRoom(accessCode, {
        ...userData,
        ready: false,
        deck: null,
        hand: [],
        character: null,
        bot: false,
      });
      setCodeInput("");
      onClose();
      setScreenLoading(true);
      const timeout = setTimeout(() => {
        navigate("/lobby");
      }, 500); // small delay, but lets TransitionOverlay take over

      return () => clearTimeout(timeout);

    } catch (err) {
      console.warn("Invalid code or room full");
    }
  };

  const handleJoinRoom = async (accessCode) => {
    try {
      const q = query(
        collection(db, "rooms"),
        where("accessCode", "==", accessCode)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        console.warn("Room not found");
        return;
      }
      const roomDoc = snapshot.docs[0];
      const roomData = roomDoc.data();

      const humanPlayers = roomData.players.length;
      const maxHumansAllowed = Math.floor(roomData.amountOfPlayers / 2);

      if (roomData.pve && humanPlayers >= maxHumansAllowed) {
        setMessage("Join failed: PvE mode only allows a limited number of human players.");
        setError(true);
        return;
      }

      if (roomData.players.length >= roomData.amountOfPlayers) {
        setMessage("Join failed: Room is full.");
        setError(true);
        return;
      }
      await joinAndSetRoom(accessCode, {
        ...userData,
        ready: false,
        deck: null,
        hand: [],
        character: null,
        bot: false,
      });
      onClose();
      setScreenLoading(true);
      const timeout = setTimeout(() => {
        navigate("/lobby");
      }, 500); // small delay, but lets TransitionOverlay take over

      return () => clearTimeout(timeout);

    } catch (err) {
      console.warn("Join failed:", err.message);
    }
  };

  const handleClose = () => {
    clearRooms();
    onClose();
  };

  return (
    <Modal
      open={visible}
      onClose={handleClose}
      closeAfterTransition
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box sx={styles.modalContainer}>
        <img
          src={backgroundImage}
          alt="Background"
          style={styles.backgroundImage}
        />
        <Toast
          onFinish={() => { }}
          timeTilClose={3000}
        />


        <Box sx={styles.modalContent}>
          <Typography
            variant="h6"
            sx={{ color: "#fff", fontWeight: "bold", mb: 2 }}
          >
            Join a Room
          </Typography>

          <Box sx={styles.codeRow}>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              placeholder="Enter 7-digit Room Code"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                flex: 1,
              }}
            />
            <NeoButton
              text="Join"
              color={Theme.palette.primary.main}
              width={25}
              onClick={handleCodeJoin}
            />
          </Box>

          <Typography sx={{ color: "#fff", mt: 2, mb: 1 }}>
            Public Rooms
          </Typography>

          {loading ? (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <CircularProgress size={30} sx={{ color: "#fff" }} />
              <Typography sx={{ color: "#fff", mt: 2 }}>
                Loading rooms...
              </Typography>
            </Box>
          ) : (
            <Box sx={styles.scrollArea}>
              {rooms.length === 0 ? (
                <Typography sx={{ color: "#fff" }}>
                  No rooms available
                </Typography>
              ) : (
                rooms.map((room) => {
                  const roomOwner = room.players.find(
                    (player) => player.id === room.roomOwner
                  );
                  const roomOwnerName = roomOwner
                    ? roomOwner.username
                    : "Unknown";
                  return (
                    <Box key={room.id} sx={styles.roomRow}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                          @{roomOwnerName}
                        </Typography>
                        <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                          Access Code: {room.accessCode}
                        </Typography>
                        <Typography sx={{ color: "#ccc" }}>
                          Bots: {room.botFill ? "Yes" : "No"} | Players:{" "}
                          {room.players.length}/{room.amountOfPlayers}
                        </Typography>
                      </Box>
                      <NeoButton
                        text="Join"
                        width={25}
                        color={Theme.palette.primary.main}
                        onClick={() => handleJoinRoom(room.accessCode)}
                      />
                    </Box>
                  );
                })
              )}
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              bottom: "-20px",
              left: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <NeoButton
              text="Cancel"
              color={Theme.palette.secondary.main}
              width={49}
              onClick={onClose}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const styles = {
  modalContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 2,
    height: { xs: "70%", md: "60%" },
    width: { xs: "95%", md: "30%" },
    position: "absolute",
    outline: "none",
  },
  backgroundImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  modalContentWrapper: {
    width: modalWidth,
    height: modalHeight,
  },
  modalContent: {
    width: modalWidth,
    height: modalHeight,
    paddingTop: "5.6%",
    paddingLeft: "8%",
    paddingRight: "8%",
    paddingBottom: "12%",
    borderRadius: 16,
    elevation: 5,
    borderColor: "#fff",
    position: "relative",
  },
  codeRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    mb: 2,
  },
  scrollArea: {
    mt: 1,
    maxHeight: 300,
    overflowY: "auto",
  },
  roomRow: {
    backgroundColor: "#4e2a9b",
    p: 1,
    borderRadius: 1,
    mb: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
  },
  buttonRow: {
    mt: 2,
  },
};

export default JoinRoomModal;
